import * as React from "react";
import { computed, observable, action } from "mobx"
import { observer, inject } from "mobx-react";
import { AreaBody } from "../General/AreaBody";
import { Strings } from "../Stores/StringsStore";
import { AppStore } from "../Stores/AppStore";
import { DialogStore, DialogResult } from "../Stores/DialogStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { ShowAreaAsideButton } from "../Buttons/ShowAreaAsideButton";
import { SaveButton } from "../Buttons/SaveButton";
import { DeleteButton } from "../Buttons/DeleteButton";
import { CloseButton } from "../Buttons/CloseButton";
import { Toolbar } from "../General/Toolbar";
import * as PortalManagement from "../PortalManagement";
import { format } from "../Utilities";
import { Urls } from "../Urls";
import * as UiStore from "../States/AreaUiInfo";
import { PortalStore } from "../Stores/PortalStore"
import { newGuid } from "../Utilities";
import * as SettingsManagement from "../SettingsManagement";
import * as DocumentReader from "../DocumentReader";
import { RouteComponentProps } from "react-router-dom";
import { InputTextComponent } from "../Inputs/InputTextComponent";

interface IPortalProps extends RouteComponentProps<IPortalPropsParams>, UiStore.IAreaUiInfo {
	store?: PortalStore;
}

interface IPortalPropsParams {
	id: string;
}

@inject('store')
@inject('areaUiInfo')
@observer
export class PortalEdit extends React.Component<IPortalProps, {}> {

	store: PortalStore;
	@observable model = new PortalManagement.PortalSettings();
	@observable hash: string;
	curPortalSettingsId: string;

	componentWillMount() {
		this.store = this.props.store;
		this.load();
		this.setEditMode();
	}

	componentDidUpdate(prevProps: IPortalProps) {
		if (this.props.match.params.id != prevProps.match.params.id) {
			this.load();
		}
		this.setEditMode();
	}

	@computed get canSave(): boolean {
		let canSave = this.isValidName && this.model.url.isValid && this.isValidUserName && this.isValidPassword && (this.hash != JSON.stringify(this.model));
		AppStore.isDirtyMode = canSave;
		AppStore.isDirtyChange = this.hash != JSON.stringify(this.model);
		AppStore.saveOnLeave = this.saveOnDirtyLeave;
		return canSave;
	}

	@computed get canSetSelection(): boolean {
		return AppStore.context.sessionDetails.portalSettingsId != this.model.id;
	}

	@computed get isValidName(): boolean {
		return this.model.name != "";
	}

	@computed get isValidUserName(): boolean {
		return this.model.userName != "";
	}

	@computed get isValidPassword(): boolean {
		return this.model.password != "";
	}

	@action setEditMode = () => {
		this.store.isEditMode = this.store.model.some(item => item.id === this.props.match.params.id);
	}

	load() {
		AppStore.isBusyModelLoading = true;
		PortalManagement.getPortalSettingsAsync(this.props.match.params.id).then(model => {
			this.store.currentFocusedItem = this.props.match.params.id;
			this.model = model;
			this.hash = JSON.stringify(this.model);
			NotificationStore.clearMessage();

			PortalManagement.getCurrentPortalSettingsAsync().then(portalSettings => {
				this.curPortalSettingsId = portalSettings.id
			});

			AppStore.isBusyModelLoading = false;
		}).catch(error => {
			DialogStore.showError(Strings.general.errorDialogTitle, error);
			AppStore.isBusyModelLoading = false;
		});
	}

	handleSave = () => {
		this.saveModel();
	}

	saveOnDirtyLeave = (onSaveCompleted: () => void) => {
		this.saveModel(onSaveCompleted);
	}

	private setCurrentPortal(portalSettingsId: string): void {
		PortalManagement.setCurrentPortalSettingsAsync(portalSettingsId).then(portalSettings => {
			AppStore.init().then(() => {
				AppStore.history.replace("/");
				location.reload(true);
				DocumentReader.restoreDownloadedFilesAsync()
					.catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));
			}).catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));
		}).catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));
	}

	private saveModel(onSaveCompleted?: () => void) {
		if (this.canSave) {
			AppStore.isBusy = true;
			PortalManagement.savePortalSettingsAsync(this.model).then(settings => {
				this.store.isEditMode = true;
				this.model = settings;
				if (!settings.isValid) {
					AppStore.isBusy = false;
					DialogStore.showError(Strings.general.errorDialogTitle, settings.validationMessage);
				}
				else {
					this.hash = JSON.stringify(this.model);

					AppStore.updatePortals().then(() => {
						AppStore.isBusy = false;
						if (AppStore.portals.length == 1 || AppStore.context.portalSettings.id === this.model.id) {
							this.setCurrentPortal(this.model.id);
						}
					}).catch((error: any) => {
						AppStore.isBusy = false;
					});

					this.store.search();
					NotificationStore.showSuccessMessage(Strings.general.saveSuccessMessage);
				}
			}).catch(error => {
				AppStore.isBusy = false;
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
		}
	}

	handleDelete = () => {
		DialogStore.show(format(Strings.general.dialogDeleteTitle, this.model.name), format(Strings.general.dialogDeleteText, this.model.name)).then(result => {
			if (result == DialogResult.Yes) {
				AppStore.isBusy = true;
				PortalManagement.deletePortalSettingsAsync(this.model.id).then(status => {
					this.store.search();
					NotificationStore.showSuccessMessage(Strings.general.deleteSuccessMessage);
					AppStore.history.replace(Urls.portals);
					this.store.isEditMode = false;

					AppStore.context.applicationTitle = "";
					Urls.reloadBranding();

					AppStore.updatePortals().then(() => {
						AppStore.isBusy = false;

						// go to "add new portal" if the very last one was deleted
						if (AppStore.portals.length == 0) {
							// set online false to force status update
							AppStore.isSystemOnline = false;
							AppStore.keepOnPage(Urls.portals + "/" + newGuid(), true);
						}
						else if (this.model.id == this.curPortalSettingsId) {
							// check if currently selected portal is deleted -> select another one
							this.setCurrentPortal(AppStore.portals[0].id);
						}
					}).catch((error: any) => {
						AppStore.isBusy = false;
					});
				}).catch(error => {
					AppStore.isBusy = false;
					DialogStore.showError(Strings.general.errorDialogTitle, error);
				});
			}
		});
	}

	handleCancel = () => {
		AppStore.keepOnPage(Urls.portals, null, this.store.areaUiInfo.closeExpand);
		//AppStore.history.replace(Urls.portals);
	}

	handleNameChange = (event: React.FormEvent<any>) => {
		this.model.name = (event.target as any).value as string;
		this.model.isValid = true;
		this.model.validationMessage = "";
		NotificationStore.clearMessage();
	}

	handleUrlChange = () => {
		this.model.isValid = true;
		this.model.validationMessage = "";
		NotificationStore.clearMessage();
	}

	handleUserNameChange = (event: React.FormEvent<any>) => {
		this.model.userName = (event.target as any).value as string;
		this.model.isValid = true;
		this.model.validationMessage = "";
		NotificationStore.clearMessage();
	}

	handlePasswordChange = (event: React.FormEvent<any>) => {
		this.model.password = (event.target as any).value as string;
		this.model.isValid = true;
		this.model.validationMessage = "";
		NotificationStore.clearMessage();
	}

	@computed get areaAsideButtonTooltip() {
		return this.props.areaUiInfo.isCollapsed ? Strings.settings.portalEditAreaAsideButtonCollapsedTooltip : Strings.settings.portalEditAreaAsideButtonExpandedTooltip;
	}

	render() {
		return <AreaBody loading={AppStore.isBusyModelLoading}>
			<Toolbar>
				<ShowAreaAsideButton title={this.areaAsideButtonTooltip} />
				<SaveButton onClick={this.handleSave} disabled={!this.canSave} />
				<DeleteButton onClick={this.handleDelete} disabled={!this.store.isEditMode} />
				<span className="buttongroup is--buttongroup-right">
					<CloseButton onClick={this.handleCancel} />
				</span>
			</Toolbar>
			<div className="area-grid__body-content">
				<h1>{this.store.isEditMode ? Strings.settings.portalsEditLabel : Strings.settings.portalsAddLabel}</h1>
				<div>
					<label>
						<span>{Strings.settings.portalEditName}</span>
						<input type="text" placeholder={Strings.settings.portalEditNamePlaceHolder} value={this.model.name} onChange={this.handleNameChange} className={this.model.name && !this.isValidName ? "is--input-invalid" : ""} />
					</label>
				</div>
				<div>
					<label>
						<span>{Strings.settings.portalUrlLabel}</span>
						<InputTextComponent model={this.model.url} onChange={this.handleUrlChange} required={true} />
					</label>
				</div>
				<div>
					<label>
						<span>{Strings.settings.usernameLabel}</span>
						<input type="text" placeholder={Strings.settings.usernamePlaceholder} value={this.model.userName} onChange={this.handleUserNameChange} className={this.model.userName && !this.isValidUserName ? "is--input-invalid" : ""} />
					</label>
				</div>
				<div>
					<label>
						<span>{Strings.settings.passwordLabel}</span>
						<input type="password" placeholder={Strings.settings.passwordPlaceholder} value={this.model.password} onChange={this.handlePasswordChange} className={this.model.password && !this.isValidPassword ? "is--input-invalid" : ""} />
					</label>
				</div>
			</div>
		</AreaBody>;
	}
}
